import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useSpring, animated } from "react-spring";
import { useCarousel } from "@shared/hooks/useCarousel";

interface CarouselProps {
  items: React.ReactNode[];
  autoplayDelay?: number;
}

const Carousel: React.FC<CarouselProps> = ({ items, autoplayDelay = 5000 }) => {
  const { activeIndex, goToNext, goToPrev, goToIndex } = useCarousel({
    itemCount: items.length,
    autoplayDelay,
  });

  const fadeSpring = useSpring({
    opacity: 1,
    config: {
      tension: 200,
      friction: 36,
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      goToNext();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      goToPrev();
    }
  };

  const handleSlideClick = () => {
    goToNext();
  };

  return (
    <Styled.Root tabIndex={0} onKeyDown={handleKeyDown} role="region" aria-label="Image carousel">
      <Styled.SlidesContainer
        onClick={handleSlideClick}
        role="button"
        tabIndex={-1}
        aria-label="Click to advance to next slide"
      >
        {items.map((item, index) => (
          <Styled.Slide
            key={index}
            as={animated.div}
            style={{
              opacity: index === activeIndex ? 1 : 0,
              position: index === activeIndex ? "relative" : "absolute",
              top: 0,
              left: 0,
              right: 0,
              transition: "opacity 0.4s ease-in-out",
            }}
          >
            {item}
          </Styled.Slide>
        ))}
      </Styled.SlidesContainer>

      <Styled.DotsContainer>
        {items.map((_, index) => (
          <Styled.Dot
            key={index}
            active={index === activeIndex}
            onClick={() => goToIndex(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </Styled.DotsContainer>
    </Styled.Root>
  );
};

export default Carousel;

/* Styled Components
======================================================= */
let Styled: any = {};

Styled.Root = styled.div`
  ${(props) => {
    const t: any = props.theme;
    return css`
      position: relative;
      width: 100%;
      overflow: hidden;
      outline: none;

      &:focus-visible {
        box-shadow: 0 0 0 2px ${t.dark ? t.color.gray[400] : t.color.gray[500]};
      }
    `;
  }}
`;

Styled.SlidesContainer = styled.div`
  ${() => {
    return css`
      position: relative;
      cursor: pointer;
      min-height: 200px;
    `;
  }}
`;

Styled.Slide = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  box-sizing: border-box;
`;

Styled.DotsContainer = styled.div`
  ${(props) => {
    const t: any = props.theme;
    return css`
      display: flex;
      justify-content: center;
      gap: ${t.spacing[2]};
      ${t.mt(6)}
    `;
  }}
`;

Styled.Dot = styled.button<{ active: boolean }>`
  ${(props) => {
    const t: any = props.theme;
    return css`
      width: 12px;
      height: 12px;
      border-radius: 50%;
      border: none;
      cursor: pointer;
      transition: background-color 0.2s ease;
      background-color: ${props.active
        ? t.dark
          ? t.color.gray[300]
          : t.color.gray[600]
        : t.dark
        ? t.color.gray[600]
        : t.color.gray[300]};

      &:hover {
        background-color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};
      }

      &:focus-visible {
        outline: 2px solid ${t.dark ? t.color.gray[400] : t.color.gray[500]};
        outline-offset: 2px;
      }
    `;
  }}
`;
